import { useCallback, useEffect, useState } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import useActionDuration from '../../../../../common/hooks/useActionDuration';
import { getRandomId } from '../../../../../common/utils/utils';
import { EVENTS } from '../../../../labelling-v2/atoms/label-store-dispatch/events';
import { useStageAtoms } from '../../../../labelling-v2/components/stage-scope/hooks';
import { ANNOTATION_TYPE, SKELETON_TOOLS, STAGE_EVENTS } from '../../../constants';
import useStage from '../../../providers/hooks/use-stage';
import type { StagePointerEventT } from '../../../providers/stage-provider';
import type { SkeletonAnnotationT, SkeletonConfigT } from '../../../types';
import { isEdgeAlreadyExistingInSkeletonAnnotation } from '../../../utils';
import { isLabelingMode } from '../../toolbar/utils';
import { generateSkeleton } from './util';
const isValidSkeletonTool = (selectedTool: string, templateConfigs: SkeletonTemplateConfigT[]): boolean => {
  const isTemplateTool = templateConfigs.some(config => config.toolType === selectedTool);
  return selectedTool === SKELETON_TOOLS.FREE_HAND_SKELETON || isTemplateTool;
};
export type SkeletonTemplateConfigT = {
  template: SkeletonConfigT;
  toolType: string;
};
export const useDrawSkeleton = ({
  drawingSkeleton,
  setDrawingSkeleton,
  selectedVertexIdx,
  hoveredVertexIdx,
  isDrawing,
  templateConfigs = []
}: {
  drawingSkeleton: SkeletonAnnotationT | null;
  selectedVertexIdx: number | null;
  hoveredVertexIdx: number | null;
  isDrawing?: boolean;
  setDrawingSkeleton: (annotation: SkeletonAnnotationT | null) => void;
  templateConfigs?: SkeletonTemplateConfigT[];
}) => {
  const {
    stage,
    addAnnotation,
    getStageDimension
  } = useStage();
  const {
    editorModeAtom,
    brushToolSettingsAtom,
    dispatchAtom,
    selectedAnnotationLabelAtom
  } = useStageAtoms();
  const editorMode = useAtomValue(editorModeAtom);
  const brushToolSettings = useAtomValue(brushToolSettingsAtom);
  const annotationLabel = useAtomValue(selectedAnnotationLabelAtom);
  const dispatchAction = useSetAtom(dispatchAtom);
  const {
    start,
    end
  } = useActionDuration();
  const [currentlyDrawnEdge, setCurrentlyDrawnEdge] = useState<[[number, number], [number, number]] | null>(null);
  const onTemplateSkeletonClick = useCallback((stageEvent: StagePointerEventT) => {
    const {
      selectedTool
    } = brushToolSettings;
    const templateConfig = templateConfigs.find(config => config.toolType === selectedTool);
    if (!templateConfig) {
      return;
    }
    const {
      mousePos
    } = stageEvent.detail;
    if (!annotationLabel) return;

    // If there's no existing drawing skeleton, create one from template
    if (!drawingSkeleton) {
      start();
      const [stageWidth, stageHeight] = getStageDimension();

      // Generate the skeleton from template centered at the clicked position
      const skeletonConfig = generateSkeleton({
        centerX: mousePos[0],
        centerY: mousePos[1],
        template: templateConfig.template,
        stageWidth,
        stageHeight
      });
      const skeletonAnnotation: SkeletonAnnotationT = {
        annotationType: ANNOTATION_TYPE.SKELETON,
        annotationLabel,
        data: skeletonConfig.skeleton
      };
      const isHumanSkeletonTool = selectedTool === SKELETON_TOOLS.HUMAN_SKELETON_TOOL;
      if (isHumanSkeletonTool) {
        addAnnotation(skeletonAnnotation, {
          actionDuration: end(),
          vertex: skeletonConfig.skeleton.nodes.length
        });
      } else {
        setDrawingSkeleton(skeletonAnnotation);
        dispatchAction({
          type: EVENTS.SET_BRUSH_TOOL_SETTINGS,
          brushToolSettings: {
            selectedTool: SKELETON_TOOLS.FREE_HAND_SKELETON,
            eraserSelected: false
          }
        });
      }
    }
  }, [brushToolSettings, annotationLabel, start, end, addAnnotation, setDrawingSkeleton, templateConfigs, drawingSkeleton, dispatchAction, getStageDimension]);
  const onNodeAddPointerDown = useCallback((stageEvent: StagePointerEventT) => {
    const {
      selectedTool
    } = brushToolSettings;
    if (selectedTool !== SKELETON_TOOLS.FREE_HAND_SKELETON) {
      return;
    }
    const {
      mousePos
    } = stageEvent.detail;
    // TODO: handle case where annotationLabel is not selected
    if (!annotationLabel) return;
    const currentNodes = drawingSkeleton?.data.nodes ?? [];
    if (currentNodes.length === 0) {
      start();
    }
    const updatedSkeletonAnnotation: SkeletonAnnotationT = {
      annotationType: ANNOTATION_TYPE.SKELETON,
      annotationLabel,
      data: {
        nodes: [...currentNodes, {
          point: [mousePos[0], mousePos[1]],
          id: getRandomId()
        }],
        edges: drawingSkeleton?.data.edges ?? []
      }
    };
    setDrawingSkeleton(updatedSkeletonAnnotation);
  }, [brushToolSettings, annotationLabel, drawingSkeleton?.data.edges, drawingSkeleton?.data.nodes, setDrawingSkeleton, start]);
  const onPointerDown = useCallback((stageEvent: StagePointerEventT) => {
    const {
      selectedTool
    } = brushToolSettings;
    const templateConfig = templateConfigs.find(config => config.toolType === selectedTool);
    if (templateConfig) {
      onTemplateSkeletonClick(stageEvent);
      return;
    }
    if (selectedTool === SKELETON_TOOLS.FREE_HAND_SKELETON) {
      onNodeAddPointerDown(stageEvent);
      return;
    }
  }, [onTemplateSkeletonClick, onNodeAddPointerDown, brushToolSettings, templateConfigs]);
  const onEdgeAddDrag = useCallback((stageEvent: StagePointerEventT) => {
    const {
      selectedTool
    } = brushToolSettings;
    if (!isValidSkeletonTool(selectedTool, templateConfigs)) {
      return;
    }

    // Nodes should be drawn before edges can be drawn.
    if (!drawingSkeleton) {
      return;
    }
    // Vertex needs to be selected for starting position of the edge.
    if (selectedVertexIdx === null) {
      return;
    }
    // On start we will initialize edge with starting coordinate of the vertex
    if (!currentlyDrawnEdge) {
      // @ts-expect-error todo(ts-migration) TS2339 Property 'point' does not exist on type 'NodeT | undefined'.
      const {
        point
      } = drawingSkeleton.data.nodes[selectedVertexIdx];
      const initialPoints: [[number, number], [number, number]] = [point, point];
      setCurrentlyDrawnEdge(initialPoints);
      return;
    }
    const updatedEdge = stageEvent.detail.mousePos;
    // If edge drawing has already started, then we update only the end
    setCurrentlyDrawnEdge([currentlyDrawnEdge[0], [updatedEdge[0], updatedEdge[1]]]);
  }, [currentlyDrawnEdge, drawingSkeleton, selectedVertexIdx, brushToolSettings, templateConfigs]);
  const onEdgeAddDragEnd = useCallback(() => {
    const {
      selectedTool
    } = brushToolSettings;
    if (!isValidSkeletonTool(selectedTool, templateConfigs)) {
      return;
    }

    // Reset the currently drawn edge as edge creation is done.
    setCurrentlyDrawnEdge(null);

    // No need add new vertex if drag does not end on vertex or no start vertex is selected
    if (hoveredVertexIdx === null || selectedVertexIdx === null) {
      return;
    }
    if (!drawingSkeleton) {
      return;
    }
    const endNode = drawingSkeleton.data.nodes[hoveredVertexIdx];
    const startNode = drawingSkeleton.data.nodes[selectedVertexIdx];
    if (isEdgeAlreadyExistingInSkeletonAnnotation(drawingSkeleton, {
      // @ts-expect-error todo(ts-migration) TS18048 'startNode' is possibly 'undefined'.
      startNode: startNode.id,
      // @ts-expect-error todo(ts-migration) TS18048 'endNode' is possibly 'undefined'.
      endNode: endNode.id
    })) {
      return;
    }
    const updatedDrawingSkeleton: SkeletonAnnotationT = {
      ...drawingSkeleton,
      data: {
        ...drawingSkeleton.data,
        // @ts-expect-error todo(ts-migration) TS18048 'startNode' is possibly 'undefined'.
        edges: [...drawingSkeleton.data.edges, {
          startNode: startNode.id,
          endNode: endNode.id
        }]
      }
    };
    setDrawingSkeleton(updatedDrawingSkeleton);
  }, [drawingSkeleton, hoveredVertexIdx, selectedVertexIdx, setDrawingSkeleton, brushToolSettings, templateConfigs]);
  const onDoubleCLickOnVertexToEndAnnotation = useCallback(() => {
    const {
      selectedTool
    } = brushToolSettings;
    if (!isValidSkeletonTool(selectedTool, templateConfigs)) {
      return;
    }
    if (!drawingSkeleton) {
      return;
    }
    const isTemplateTool = templateConfigs.some(config => config.toolType === selectedTool);
    if (isTemplateTool || selectedVertexIdx !== null) {
      addAnnotation(drawingSkeleton, {
        actionDuration: end(),
        vertex: drawingSkeleton.data.nodes.length
      });
      setDrawingSkeleton(null);
      setCurrentlyDrawnEdge(null);
    }
  }, [addAnnotation, drawingSkeleton, end, selectedVertexIdx, setDrawingSkeleton, brushToolSettings, templateConfigs]);
  const onRightClickDeleteSelectedNode = useCallback(() => {
    const {
      selectedTool
    } = brushToolSettings;
    if (!isValidSkeletonTool(selectedTool, templateConfigs)) {
      return;
    }
    if (selectedVertexIdx === null) {
      return;
    }
    if (!drawingSkeleton) {
      return;
    }
    const selectedNode = drawingSkeleton.data.nodes[selectedVertexIdx];
    // @ts-expect-error todo(ts-migration) TS18048 'selectedNode' is possibly 'undefined'.
    const updatedNodes = drawingSkeleton.data.nodes.filter(node => node.id !== selectedNode.id);
    const updatedEdges = drawingSkeleton.data.edges.filter(
    // @ts-expect-error todo(ts-migration) TS18048 'selectedNode' is possibly 'undefined'.
    edge => edge.startNode !== selectedNode.id && edge.endNode !== selectedNode.id);
    setDrawingSkeleton({
      ...drawingSkeleton,
      data: {
        edges: updatedEdges,
        nodes: updatedNodes
      }
    });
  }, [drawingSkeleton, selectedVertexIdx, setDrawingSkeleton, brushToolSettings, templateConfigs]);
  useEffect(() => {
    if (!stage || !isLabelingMode(editorMode)) {
      return;
    }
    const {
      selectedTool
    } = brushToolSettings;
    if (isDrawing && isValidSkeletonTool(selectedTool, templateConfigs)) {
      stage.addEventListener(STAGE_EVENTS.POINTER_DOWN, onPointerDown);
      if (isValidSkeletonTool(selectedTool, templateConfigs)) {
        stage.addEventListener(STAGE_EVENTS.DRAG, onEdgeAddDrag);
        stage.addEventListener(STAGE_EVENTS.DRAG_END, onEdgeAddDragEnd);
        stage.addEventListener(STAGE_EVENTS.DOUBLE_CLICK, onDoubleCLickOnVertexToEndAnnotation);
        stage.addEventListener(STAGE_EVENTS.RIGHT_CLICK, onRightClickDeleteSelectedNode);
      }
      return () => {
        stage.removeEventListener(STAGE_EVENTS.POINTER_DOWN, onPointerDown);
        stage.removeEventListener(STAGE_EVENTS.DRAG, onEdgeAddDrag);
        stage.removeEventListener(STAGE_EVENTS.DRAG_END, onEdgeAddDragEnd);
        stage.removeEventListener(STAGE_EVENTS.DOUBLE_CLICK, onDoubleCLickOnVertexToEndAnnotation);
        stage.removeEventListener(STAGE_EVENTS.RIGHT_CLICK, onRightClickDeleteSelectedNode);
      };
    }
  }, [editorMode, isDrawing, onPointerDown, onDoubleCLickOnVertexToEndAnnotation, onEdgeAddDrag, onEdgeAddDragEnd, onRightClickDeleteSelectedNode, stage, brushToolSettings, templateConfigs]);
  return {
    currentlyDrawnEdge,
    isSkeletonMode: isValidSkeletonTool(brushToolSettings.selectedTool, templateConfigs)
  };
};